import {
    CountrySlugs,
    CountryVersion,
    CountryVersionToCountrySelfname,
    CountryVersionToUTSLocale,
} from "@smile2impress/attraction-constants";
import { SupportedLanguage } from "@smile2impress/multilang";
import { excludedLocales } from "@smile2impress/location-content";
import { getLocalizedPathname } from "modules/localize-pathnames/getLocalizedPathname";

import type { HreflangAlternate } from "components/MetaTags/MetaTags.types";
import { COOKIES, CountryVersionToLocale, I18nLocale } from "constants/enum";
import { setCookie } from "utils/cookie";
import { checkIsNotFound } from "configs/unsupport-locale-routes";
import { UrlsConfig } from "configs/urlsConfig";
import {
    getCmsRedirectPathFromAlternates,
    isCmsCatchAllPathname,
} from "utils/cmsHreflang";
import { removeLeadingLocalePrefixes } from "utils/localePathUtils";

export const checkIsQuestionAlreadySeen = () =>
    document.cookie.indexOf(COOKIES.PREFERRED_REGION) !== -1;

export const getTranslatedRegions = (version: CountryVersion) => {
    return Object.entries(CountryVersion)
        .filter(([key]) => !excludedLocales.includes(key))
        .map(([_, region]) => {
            return {
                id: region,
                name: CountryVersionToCountrySelfname[region],
                isActive: region === version,
            };
        })
        .sort((region) => (region.isActive ? -1 : 0));
};

export const setPreferredRegionCookie = (version: CountryVersion) => {
    const now = new Date();
    const locale = CountryVersionToLocale[version];
    const lang = SupportedLanguage.config[locale];

    now.setMonth(now.getMonth() + 6);

    const options = { path: "/", expires: now.toUTCString() };

    setCookie(COOKIES.PREFERRED_REGION, version, options);
    setCookie(COOKIES.PREFERRED_LANG, lang, options);
    setCookie(COOKIES.NEXT_LOCALE, locale, options);
};

export const setPreferredCityCookie = (
    id: string,
    name: string,
    version: CountryVersion
) => {
    const now = new Date();
    now.setMonth(now.getMonth() + 6);
    const locale = CountryVersionToUTSLocale[version];
    const country = CountrySlugs[locale as keyof typeof CountrySlugs];
    const options = { path: "/", expires: now.toUTCString() };

    setCookie(COOKIES.GEO_CITY_ID, id, options);
    setCookie(COOKIES.GEO_CITY, name, options);
    setCookie(COOKIES.GEO_SEARCH_COUNTRY, country, options);
};

export const getRedirectPathByRegion = (
    page: UrlsConfig,
    locale: I18nLocale,
    options?: { hreflangAlternates?: HreflangAlternate[] | null }
) => {
    const resolvePath = (): string => {
        if (isCmsCatchAllPathname(page)) {
            const cmsPath = getCmsRedirectPathFromAlternates(
                options?.hreflangAlternates,
                locale,
                { isPreview: page === UrlsConfig.CMS_PREVIEW }
            );
            return cmsPath ?? "/";
        }

        // Redirect from blog article to blog hub
        if (page.includes("/blog")) {
            return "/blog";
        }

        // Redirect from exact clinic page to main
        if (page.includes("/clinic") || checkIsNotFound(page, locale)) {
            return "/";
        }

        return getLocalizedPathname(page, locale) || page;
    };

    // router.push prepends { locale }; strip any prefix so we don't emit `/uk/es/…`.
    return removeLeadingLocalePrefixes(resolvePath());
};

export const isClickedOnButton = (clickTarget: HTMLInputElement, depth = 2) => {
    const findButtonElement = (
        el: HTMLInputElement,
        iteration: number
    ): boolean => {
        if (el?.tagName === "BUTTON") return true;
        else if (iteration < depth)
            return findButtonElement(
                el?.parentNode as HTMLInputElement,
                iteration + 1
            );
        return false;
    };

    return findButtonElement(clickTarget, 1);
};
